import type { ChildDevice, ChildProfile, InternetAccessAccount, PairingCode } from '../../api/types.js';
import { formatTimestamp } from '../../core/format.js';
import { BlockWindowsCard } from './BlockWindowsCard.js';
import { InternetAccessCard } from './InternetAccessCard.js';
import { PairingCodeCard } from './PairingCodeCard.js';

/**
 * One child's row on the dashboard — the per-child body of Android's
 * `ParentDashboardScreen` `LazyColumn`: the name, its paired devices, the
 * pairing-code affordance, and the Internet-Access controls.
 */
export function ChildCard({
  child,
  devices,
  pairingCode,
  account,
  pendingPairingCode,
  cancellingPairingCode,
  deleting,
  revokingDeviceId,
  savingTopUp,
  savingDeductBonus,
  savingForceBlocked,
  savingReset,
  savingBlockWindow,
  onRequestDelete,
  onGeneratePairingCode,
  onCancelPairingCode,
  onRequestRevokeDevice,
  onTopUp,
  onDeductBonus,
  onSetForceBlocked,
  onReset,
  onAddBlockWindow,
  onRemoveBlockWindow,
}: {
  child: ChildProfile;
  devices: ChildDevice[];
  pairingCode: PairingCode | undefined;
  account: InternetAccessAccount | undefined;
  pendingPairingCode: boolean;
  cancellingPairingCode: boolean;
  deleting: boolean;
  revokingDeviceId: string | null;
  savingTopUp: boolean;
  savingDeductBonus: boolean;
  savingForceBlocked: boolean;
  savingReset: boolean;
  savingBlockWindow: boolean;
  onRequestDelete: () => void;
  onGeneratePairingCode: () => void;
  onCancelPairingCode: () => void;
  onRequestRevokeDevice: (device: ChildDevice) => void;
  onTopUp: () => void;
  onDeductBonus: () => void;
  onSetForceBlocked: (forceBlocked: boolean) => void;
  onReset: () => void;
  onAddBlockWindow: (startTime: string, endTime: string) => void;
  onRemoveBlockWindow: (blockWindowId: string) => void;
}) {
  return (
    <li className="card">
      <div className="card__header">
        <h3 className="card__title">{child.displayName}</h3>
        <button
          type="button"
          className="button button--text button--danger"
          onClick={onRequestDelete}
          disabled={deleting}
        >
          Löschen
        </button>
      </div>

      {devices.length === 0 ? (
        <p className="muted">Noch kein Gerät gekoppelt</p>
      ) : (
        <ul className="device-list">
          {devices.map((device) => (
            <li key={device.id} className="device">
              <div>
                <p className="device__label">{device.deviceLabel}</p>
                <p className="muted">
                  {device.status}
                  {device.lastSeenAt ? ` · zuletzt gesehen ${formatTimestamp(device.lastSeenAt)}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="button button--text"
                onClick={() => onRequestRevokeDevice(device)}
                disabled={revokingDeviceId === device.id}
              >
                Entkoppeln
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        className="button button--primary"
        onClick={onGeneratePairingCode}
        disabled={pendingPairingCode}
      >
        Pairing-Code erzeugen
      </button>

      {pairingCode && (
        <PairingCodeCard
          pairingCode={pairingCode}
          cancelling={cancellingPairingCode}
          onCancel={onCancelPairingCode}
        />
      )}

      <InternetAccessCard
        account={account}
        savingTopUp={savingTopUp}
        savingDeductBonus={savingDeductBonus}
        savingForceBlocked={savingForceBlocked}
        savingReset={savingReset}
        onTopUp={onTopUp}
        onDeductBonus={onDeductBonus}
        onSetForceBlocked={onSetForceBlocked}
        onReset={onReset}
      />

      <BlockWindowsCard
        account={account}
        saving={savingBlockWindow}
        onAdd={onAddBlockWindow}
        onRemove={onRemoveBlockWindow}
      />
    </li>
  );
}
